import React, { useState } from 'react';
import Spline from '@splinetool/react-spline';
import './site.css';
import { HashLink } from 'react-router-hash-link';

// Also run: npm install react-router-dom

/* ══════════════════════════════════════════════════
   GLOBAL SVG ANIMATION STYLES
   Inject once — all SVG components share these
══════════════════════════════════════════════════ */
const SvgStyles = () => (
  <style>{`
    /* Pulse / breathe */
    @keyframes svgPulse {
      0%, 100% { opacity: 0.7; transform: scale(1); }
      50%       { opacity: 1;   transform: scale(1.06); }
    }
    @keyframes svgPulseSlow {
      0%, 100% { opacity: 0.3; }
      50%       { opacity: 0.7; }
    }
    /* Orbit rotation */
    @keyframes orbit {
      from { transform: rotate(0deg); }
      to   { transform: rotate(360deg); }
    }
    @keyframes orbitRev {
      from { transform: rotate(0deg); }
      to   { transform: rotate(-360deg); }
    }
    /* Float up-down */
    @keyframes floatY {
      0%, 100% { transform: translateY(0px); }
      50%       { transform: translateY(-8px); }
    }
    /* Dash flow along path */
    @keyframes dashFlow {
      to { stroke-dashoffset: -40; }
    }
    /* Glow flicker */
    @keyframes glowFlicker {
      0%, 100% { filter: drop-shadow(0 0 4px #00E5FF); }
      50%       { filter: drop-shadow(0 0 12px #00E5FF); }
    }
    /* Progress fill */
    @keyframes progressFill {
      from { width: 0%; }
    }
    /* ECG line draw */
    @keyframes ecgDraw {
      0%   { stroke-dashoffset: 300; }
      100% { stroke-dashoffset: 0; }
    }
    /* Ripple expand */
    @keyframes ripple {
      0%   { r: 10; opacity: 0.8; }
      100% { r: 55; opacity: 0; }
    }
    /* Spin */
    @keyframes spin {
      from { transform: rotate(0deg); }
      to   { transform: rotate(360deg); }
    }
    /* Node blink */
    @keyframes nodeBlink {
      0%, 90%, 100% { opacity: 1; }
      95%            { opacity: 0.2; }
    }
    /* Fade in stagger */
    @keyframes fadeInUp {
      from { opacity: 0; transform: translateY(6px); }
      to   { opacity: 1; transform: translateY(0); }
    }

    /* Make SVGs responsive */
    .svc-svg {
      width: 100%;
      max-width: 340px;
      height: auto;
      display: block;
      margin: 0 auto;
      overflow: visible;
    }

    @media (max-width: 768px) {
      .svc-svg { max-width: 280px; }
    }
    @media (max-width: 480px) {
      .svc-svg { max-width: 240px; }
    }
  `}</style>
);

/* ══════════════════════════════════════════════════
   01 — TECH CONSULTING
   Visual: System architecture map with animated
   connecting lines flowing between nodes
══════════════════════════════════════════════════ */
const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  const top = el.getBoundingClientRect().top + window.scrollY - 50;
  window.scrollTo({ top, behavior: 'smooth' });
};
const TechConsultingSVG = () => (
  <svg viewBox="0 0 320 280" className="svc-svg" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="tc-grad" x1="0" y1="0" x2="1" y2="1">
        <stop stopColor="#7C3AED" /><stop offset="1" stopColor="#00E5FF" />
      </linearGradient>
      <filter id="tc-glow">
        <feGaussianBlur stdDeviation="3" result="b" />
        <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
      </filter>
    </defs>

    {/* Background radial */}
    <radialGradient id="tc-bg" cx="50%" cy="50%" r="50%">
      <stop offset="0%" stopColor="#7C3AED" stopOpacity="0.12" />
      <stop offset="100%" stopColor="transparent" />
    </radialGradient>
    <circle cx="160" cy="140" r="130" fill="url(#tc-bg)" />

    {/* Flowing connection lines with dash animation */}
    <g fill="none" strokeWidth="1.5" opacity="0.5">
      <line x1="160" y1="75" x2="70" y2="140" stroke="url(#tc-grad)" strokeDasharray="6 4" style={{ animation: 'dashFlow 1.5s linear infinite' }} />
      <line x1="160" y1="75" x2="250" y2="140" stroke="url(#tc-grad)" strokeDasharray="6 4" style={{ animation: 'dashFlow 1.5s linear infinite 0.3s' }} />
      <line x1="70" y1="140" x2="115" y2="215" stroke="#00E5FF" strokeDasharray="5 4" style={{ animation: 'dashFlow 1.8s linear infinite 0.5s' }} />
      <line x1="250" y1="140" x2="205" y2="215" stroke="#00E5FF" strokeDasharray="5 4" style={{ animation: 'dashFlow 1.8s linear infinite 0.8s' }} />
      <line x1="70" y1="140" x2="160" y2="215" stroke="#7C3AED" strokeDasharray="5 4" style={{ animation: 'dashFlow 2s linear infinite 0.2s' }} />
      <line x1="250" y1="140" x2="160" y2="215" stroke="#7C3AED" strokeDasharray="5 4" style={{ animation: 'dashFlow 2s linear infinite 0.6s' }} />
    </g>

    {/* Top node — STRATEGY */}
    <g filter="url(#tc-glow)" style={{ animation: 'floatY 3s ease-in-out infinite' }}>
      <rect x="110" y="42" width="100" height="46" rx="10" fill="rgba(124,58,237,0.15)" stroke="url(#tc-grad)" strokeWidth="1.8" />
      <text x="160" y="61" textAnchor="middle" fill="#00E5FF" fontSize="9" fontFamily="monospace" fontWeight="bold">STRATEGY</text>
      <text x="160" y="76" textAnchor="middle" fill="rgba(255,255,255,0.6)" fontSize="7.5" fontFamily="monospace">Architecture Review</text>
    </g>

    {/* Mid-left — ASSESS */}
    <g filter="url(#tc-glow)" style={{ animation: 'floatY 3.4s ease-in-out infinite 0.4s' }}>
      <rect x="22" y="112" width="96" height="56" rx="10" fill="rgba(0,229,255,0.08)" stroke="#00E5FF" strokeWidth="1.5" />
      <text x="70" y="132" textAnchor="middle" fill="#00E5FF" fontSize="9" fontFamily="monospace" fontWeight="bold">ASSESS</text>
      <text x="70" y="147" textAnchor="middle" fill="rgba(255,255,255,0.55)" fontSize="7" fontFamily="monospace">Risk Analysis</text>
      <text x="70" y="159" textAnchor="middle" fill="rgba(255,255,255,0.55)" fontSize="7" fontFamily="monospace">Audit</text>
    </g>

    {/* Mid-right — DESIGN */}
    <g filter="url(#tc-glow)" style={{ animation: 'floatY 2.8s ease-in-out infinite 0.8s' }}>
      <rect x="202" y="112" width="96" height="56" rx="10" fill="rgba(0,229,255,0.08)" stroke="#00E5FF" strokeWidth="1.5" />
      <text x="250" y="132" textAnchor="middle" fill="#00E5FF" fontSize="9" fontFamily="monospace" fontWeight="bold">DESIGN</text>
      <text x="250" y="147" textAnchor="middle" fill="rgba(255,255,255,0.55)" fontSize="7" fontFamily="monospace">System Design</text>
      <text x="250" y="159" textAnchor="middle" fill="rgba(255,255,255,0.55)" fontSize="7" fontFamily="monospace">Roadmap</text>
    </g>

    {/* Bottom row */}
    <g filter="url(#tc-glow)" style={{ animation: 'floatY 3.2s ease-in-out infinite 1s' }}>
      <rect x="60" y="190" width="85" height="44" rx="9" fill="rgba(124,58,237,0.12)" stroke="url(#tc-grad)" strokeWidth="1.4" />
      <text x="102" y="208" textAnchor="middle" fill="#A78BFA" fontSize="8.5" fontFamily="monospace" fontWeight="bold">MODERNIZE</text>
      <text x="102" y="222" textAnchor="middle" fill="rgba(255,255,255,0.5)" fontSize="7" fontFamily="monospace">Migration Plan</text>
    </g>
    <g filter="url(#tc-glow)" style={{ animation: 'floatY 2.6s ease-in-out infinite 0.2s' }}>
      <rect x="175" y="190" width="85" height="44" rx="9" fill="rgba(124,58,237,0.12)" stroke="url(#tc-grad)" strokeWidth="1.4" />
      <text x="217" y="208" textAnchor="middle" fill="#A78BFA" fontSize="8.5" fontFamily="monospace" fontWeight="bold">DELIVER</text>
      <text x="217" y="222" textAnchor="middle" fill="rgba(255,255,255,0.5)" fontSize="7" fontFamily="monospace">Execution Plan</text>
    </g>

    {/* Animated dots on nodes */}
    <g fill="#00E5FF" style={{ animation: 'nodeBlink 2s ease-in-out infinite' }}>
      <circle cx="160" cy="65" r="3" />
    </g>
    <circle cx="70" cy="140" r="2.5" fill="#00E5FF" opacity="0.8" style={{ animation: 'nodeBlink 2s ease-in-out infinite 0.5s' }} />
    <circle cx="250" cy="140" r="2.5" fill="#00E5FF" opacity="0.8" style={{ animation: 'nodeBlink 2s ease-in-out infinite 1s' }} />
  </svg>
);

/* ══════════════════════════════════════════════════
   02 — PRODUCT ENGINEERING
   Visual: Sprint/delivery lifecycle ring with
   animated phases: Plan → Build → Test → Ship
══════════════════════════════════════════════════ */
const ProductEngineeringSVG = () => (
  <svg viewBox="0 0 320 300" className="svc-svg" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="pe-grad" x1="0" y1="0" x2="1" y2="1">
        <stop stopColor="#7C3AED" /><stop offset="1" stopColor="#00E5FF" />
      </linearGradient>
      <filter id="pe-glow">
        <feGaussianBlur stdDeviation="4" result="b" />
        <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
      </filter>
    </defs>

    <radialGradient id="pe-bg" cx="50%" cy="50%" r="50%">
      <stop offset="0%" stopColor="#00E5FF" stopOpacity="0.08" />
      <stop offset="100%" stopColor="transparent" />
    </radialGradient>
    <circle cx="160" cy="150" r="130" fill="url(#pe-bg)" />

    {/* Outer rotating ring */}
    <circle cx="160" cy="150" r="110" fill="none" stroke="url(#pe-grad)" strokeWidth="0.5" opacity="0.3"
      strokeDasharray="8 5" style={{ animation: 'orbit 20s linear infinite', transformOrigin: '160px 150px' }} />

    {/* Inner orbit ring */}
    <circle cx="160" cy="150" r="72" fill="none" stroke="#7C3AED" strokeWidth="0.8" opacity="0.25"
      strokeDasharray="4 6" style={{ animation: 'orbitRev 15s linear infinite', transformOrigin: '160px 150px' }} />

    {/* Centre hub */}
    <g filter="url(#pe-glow)">
      <circle cx="160" cy="150" r="34" fill="rgba(124,58,237,0.18)" stroke="url(#pe-grad)" strokeWidth="2" />
      <circle cx="160" cy="150" r="20" fill="rgba(0,229,255,0.1)" stroke="#00E5FF" strokeWidth="1" />
      <text x="160" y="147" textAnchor="middle" fill="#00E5FF" fontSize="8" fontFamily="monospace" fontWeight="bold">PRODUCT</text>
      <text x="160" y="159" textAnchor="middle" fill="#00E5FF" fontSize="8" fontFamily="monospace" fontWeight="bold">LIFECYCLE</text>
    </g>

    {/* 4 Phase bubbles at compass points */}
    {/* TOP — PLAN */}
    <g filter="url(#pe-glow)" style={{ animation: 'floatY 3s ease-in-out infinite' }}>
      <circle cx="160" cy="42" r="30" fill="rgba(124,58,237,0.18)" stroke="url(#pe-grad)" strokeWidth="1.8" />
      <text x="160" y="38" textAnchor="middle" fill="#A78BFA" fontSize="8.5" fontFamily="monospace" fontWeight="bold">PLAN</text>
      <text x="160" y="50" textAnchor="middle" fill="rgba(255,255,255,0.55)" fontSize="6.5" fontFamily="monospace">Scope &amp;</text>
      <text x="160" y="60" textAnchor="middle" fill="rgba(255,255,255,0.55)" fontSize="6.5" fontFamily="monospace">Design</text>
    </g>

    {/* RIGHT — BUILD */}
    <g filter="url(#pe-glow)" style={{ animation: 'floatY 2.8s ease-in-out infinite 0.5s' }}>
      <circle cx="268" cy="150" r="30" fill="rgba(0,229,255,0.1)" stroke="#00E5FF" strokeWidth="1.8" />
      <text x="268" y="146" textAnchor="middle" fill="#00E5FF" fontSize="8.5" fontFamily="monospace" fontWeight="bold">BUILD</text>
      <text x="268" y="157" textAnchor="middle" fill="rgba(255,255,255,0.55)" fontSize="6.5" fontFamily="monospace">Full-Stack</text>
      <text x="268" y="167" textAnchor="middle" fill="rgba(255,255,255,0.55)" fontSize="6.5" fontFamily="monospace">Eng.</text>
    </g>

    {/* BOTTOM — SHIP */}
    <g filter="url(#pe-glow)" style={{ animation: 'floatY 3.4s ease-in-out infinite 1s' }}>
      <circle cx="160" cy="258" r="30" fill="rgba(124,58,237,0.18)" stroke="url(#pe-grad)" strokeWidth="1.8" />
      <text x="160" y="254" textAnchor="middle" fill="#A78BFA" fontSize="8.5" fontFamily="monospace" fontWeight="bold">SHIP</text>
      <text x="160" y="265" textAnchor="middle" fill="rgba(255,255,255,0.55)" fontSize="6.5" fontFamily="monospace">Deploy &amp;</text>
      <text x="160" y="275" textAnchor="middle" fill="rgba(255,255,255,0.55)" fontSize="6.5" fontFamily="monospace">Scale</text>
    </g>

    {/* LEFT — TEST */}
    <g filter="url(#pe-glow)" style={{ animation: 'floatY 3.1s ease-in-out infinite 0.8s' }}>
      <circle cx="52" cy="150" r="30" fill="rgba(0,229,255,0.1)" stroke="#00E5FF" strokeWidth="1.8" />
      <text x="52" y="146" textAnchor="middle" fill="#00E5FF" fontSize="8.5" fontFamily="monospace" fontWeight="bold">TEST</text>
      <text x="52" y="157" textAnchor="middle" fill="rgba(255,255,255,0.55)" fontSize="6.5" fontFamily="monospace">QA &amp;</text>
      <text x="52" y="167" textAnchor="middle" fill="rgba(255,255,255,0.55)" fontSize="6.5" fontFamily="monospace">Review</text>
    </g>

    {/* Animated arrows between phases */}
    <g fill="none" stroke="url(#pe-grad)" strokeWidth="1.5" opacity="0.6">
      <path d="M176 70 Q210 90 238 122" strokeDasharray="5 4" style={{ animation: 'dashFlow 1.5s linear infinite' }} />
      <path d="M238 178 Q210 210 183 230" strokeDasharray="5 4" style={{ animation: 'dashFlow 1.5s linear infinite 0.5s' }} />
      <path d="M137 230 Q110 210 82 178" strokeDasharray="5 4" style={{ animation: 'dashFlow 1.5s linear infinite 1s' }} />
      <path d="M82 122 Q110 90 144 70" strokeDasharray="5 4" style={{ animation: 'dashFlow 1.5s linear infinite 1.5s' }} />
    </g>

    {/* Orbit dot */}
    <circle r="5" fill="#00E5FF" opacity="0.9" filter="url(#pe-glow)"
      style={{ animation: 'orbit 8s linear infinite', transformOrigin: '160px 150px' }}>
      <animateMotion dur="8s" repeatCount="indefinite" path="M0,-110 A110,110 0 1,1 0,110 A110,110 0 1,1 0,-110" />
    </circle>
  </svg>
);

/* ══════════════════════════════════════════════════
   03 — CLOUD & DEVOPS
   Visual: Layered cloud stacks with clear CI/CD
   pipeline steps below, arrows flowing left→right
══════════════════════════════════════════════════ */
const CloudDevOpsSVG = () => (
  <svg viewBox="0 0 320 280" className="svc-svg" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="cd-grad" x1="0" y1="0" x2="1" y2="1">
        <stop stopColor="#7C3AED" /><stop offset="1" stopColor="#00E5FF" />
      </linearGradient>
      <linearGradient id="cd-grad2" x1="0" y1="0" x2="1" y2="0">
        <stop stopColor="#00E5FF" /><stop offset="1" stopColor="#7C3AED" />
      </linearGradient>
      <filter id="cd-glow">
        <feGaussianBlur stdDeviation="4" result="b" />
        <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
      </filter>
      <marker id="cd-arrow" markerWidth="8" markerHeight="8" refX="4" refY="4" orient="auto">
        <path d="M1,1 L7,4 L1,7 Z" fill="#00E5FF" opacity="0.9" />
      </marker>
    </defs>

    {/* Cloud shape — built from overlapping circles, clearly a cloud */}
    <g filter="url(#cd-glow)" style={{ animation: 'floatY 4s ease-in-out infinite' }}>
      {/* Main cloud body */}
      <ellipse cx="160" cy="95" rx="72" ry="36" fill="rgba(124,58,237,0.14)" stroke="url(#cd-grad)" strokeWidth="1.8" />
      {/* Left bump */}
      <circle cx="102" cy="90" r="26" fill="rgba(124,58,237,0.10)" stroke="url(#cd-grad)" strokeWidth="1.4" />
      {/* Right bump */}
      <circle cx="210" cy="90" r="22" fill="rgba(124,58,237,0.10)" stroke="url(#cd-grad)" strokeWidth="1.4" />
      {/* Top bump */}
      <circle cx="155" cy="68" r="22" fill="rgba(124,58,237,0.12)" stroke="url(#cd-grad)" strokeWidth="1.4" />
      {/* Cover bottom seam */}
      <rect x="78" y="95" width="164" height="28" fill="rgba(10,5,30,0.01)" />
      {/* Cloud label */}
      <text x="160" y="94" textAnchor="middle" fill="#00E5FF" fontSize="9" fontFamily="monospace" fontWeight="bold">AWS / CLOUD</text>
      <text x="160" y="107" textAnchor="middle" fill="rgba(255,255,255,0.5)" fontSize="7" fontFamily="monospace">Serverless · Containers · IaC</text>
    </g>

    {/* Drop lines from cloud to pipeline */}
    <g stroke="#00E5FF" strokeWidth="1" opacity="0.3" strokeDasharray="3 3">
      <line x1="90" y1="126" x2="48" y2="168" />
      <line x1="130" y1="128" x2="116" y2="168" />
      <line x1="175" y1="128" x2="200" y2="168" />
      <line x1="220" y1="126" x2="270" y2="168" />
    </g>

    {/* CI/CD Pipeline — horizontal row of clear labeled steps */}
    <g>
      {/* Pipeline track */}
      <line x1="24" y1="195" x2="296" y2="195" stroke="url(#cd-grad2)" strokeWidth="1.5" opacity="0.4"
        strokeDasharray="6 4" style={{ animation: 'dashFlow 2s linear infinite' }} />

      {[
        { x: 36, label: 'CODE', sub: 'Git Push', col: '#A78BFA' },
        { x: 98, label: 'BUILD', sub: 'CI Runner', col: '#00E5FF' },
        { x: 162, label: 'TEST', sub: 'Auto QA', col: '#A78BFA' },
        { x: 224, label: 'STAGE', sub: 'Preview', col: '#00E5FF' },
        { x: 286, label: 'PROD', sub: 'Live 🚀', col: '#00E5FF' },
      ].map(({ x, label, sub, col }) => (
        <g key={label} filter="url(#cd-glow)">
          {/* Arrow connector */}
          {x < 280 && <line x1={x + 20} y1="195" x2={x + 44} y2="195" stroke={col} strokeWidth="1.5"
            markerEnd="url(#cd-arrow)" opacity="0.7" />}
          {/* Stage circle */}
          <circle cx={x} cy="195" r="18" fill="rgba(10,5,30,0.5)" stroke={col} strokeWidth="1.6"
            style={{ animation: `svgPulse ${2.5 + (x / 100)}s ease-in-out infinite` }} />
          <text x={x} y="192" textAnchor="middle" fill={col} fontSize="7.5" fontFamily="monospace" fontWeight="bold">{label}</text>
          <text x={x} y="204" textAnchor="middle" fill="rgba(255,255,255,0.45)" fontSize="6" fontFamily="monospace">{sub}</text>
        </g>
      ))}
    </g>

    {/* Infrastructure icons below */}
    <g opacity="0.75" style={{ animation: 'fadeInUp 1s ease-out forwards' }}>
      {[
        { x: 55, icon: '⚙', label: 'Terraform' },
        { x: 130, icon: '📦', label: 'Docker' },
        { x: 200, icon: '🔍', label: 'Observe' },
        { x: 270, icon: '💰', label: 'Cost Opt' },
      ].map(({ x, icon, label }) => (
        <g key={label}>
          <text x={x} y="242" textAnchor="middle" fontSize="14">{icon}</text>
          <text x={x} y="258" textAnchor="middle" fill="rgba(255,255,255,0.4)" fontSize="7" fontFamily="monospace">{label}</text>
        </g>
      ))}
    </g>
  </svg>
);

/* ══════════════════════════════════════════════════
   04 — AI / LLM (original neural net, enhanced)
══════════════════════════════════════════════════ */
const AIDrivenSVG = () => (
  <svg viewBox="0 0 320 280" className="svc-svg" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="ai-grad" x1="0" y1="0" x2="1" y2="1">
        <stop stopColor="#7C3AED" /><stop offset="1" stopColor="#00E5FF" />
      </linearGradient>
      <filter id="ai-glow">
        <feGaussianBlur stdDeviation="6" result="b" />
        <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
      </filter>
    </defs>

    <radialGradient id="ai-bg" cx="50%" cy="50%" r="50%">
      <stop offset="0%" stopColor="#7C3AED" stopOpacity="0.2" />
      <stop offset="100%" stopColor="transparent" />
    </radialGradient>
    <circle cx="160" cy="140" r="120" fill="url(#ai-bg)" style={{ animation: 'svgPulseSlow 4s ease-in-out infinite' }} />

    {/* Connections */}
    <g opacity="0.35" stroke="url(#ai-grad)" fill="none">
      {/* Input → Hidden */}
      {[55, 110, 165, 220].map(iy => (
        [90, 140, 190].map(hy => (
          <line key={`${iy}-${hy}`} x1="55" y1={iy} x2="160" y2={hy}
            strokeWidth="1" strokeDasharray="4 3"
            style={{ animation: `dashFlow ${1.5 + (iy / 300)}s linear infinite` }} />
        ))
      ))}
      {/* Hidden → Output */}
      {[90, 140, 190].map(hy => (
        [105, 175].map(oy => (
          <line key={`${hy}-${oy}`} x1="160" y1={hy} x2="265" y2={oy}
            strokeWidth="1.2" strokeDasharray="4 3"
            style={{ animation: `dashFlow ${1.8 + (hy / 300)}s linear infinite` }} />
        ))
      ))}
    </g>

    {/* Input layer label */}
    <text x="38" y="32" textAnchor="middle" fill="#00E5FF" fontSize="8" fontFamily="monospace" opacity="0.6">INPUT</text>

    {/* Input nodes */}
    <g filter="url(#ai-glow)">
      {[55, 110, 165, 220].map((y, i) => (
        <g key={y} style={{ animation: `svgPulse ${2.5 + i * 0.4}s ease-in-out infinite ${i * 0.3}s` }}>
          <circle cx="55" cy={y} r="13" fill="rgba(0,229,255,0.1)" stroke="#00E5FF" strokeWidth="1.8" />
          <text x="55" y={y + 4} textAnchor="middle" fill="#00E5FF" fontSize="7.5" fontFamily="monospace">
            {['DATA', 'RAG', 'API', 'FLOW'][i]}
          </text>
        </g>
      ))}
    </g>

    {/* Hidden layer */}
    <g filter="url(#ai-glow)">
      {[90, 140, 190].map((y, i) => (
        <g key={y} style={{ animation: `svgPulse ${2 + i * 0.3}s ease-in-out infinite ${i * 0.5}s` }}>
          <circle cx="160" cy={y} r="18" fill="rgba(124,58,237,0.2)" stroke="url(#ai-grad)" strokeWidth="2.2" />
          <text x="160" y={y + 4} textAnchor="middle" fill="#A78BFA" fontSize="7" fontFamily="monospace" fontWeight="bold">
            {['LLM', 'AGENT', 'PIPE'][i]}
          </text>
        </g>
      ))}
    </g>

    {/* Output layer label */}
    <text x="280" y="32" textAnchor="middle" fill="#00E5FF" fontSize="8" fontFamily="monospace" opacity="0.6">OUTPUT</text>

    {/* Output nodes */}
    <g filter="url(#ai-glow)">
      {[105, 175].map((y, i) => (
        <g key={y} style={{ animation: `svgPulse ${2.8 + i * 0.4}s ease-in-out infinite ${i * 0.6}s` }}>
          <circle cx="265" cy={y} r="15" fill="rgba(0,229,255,0.12)" stroke="#00E5FF" strokeWidth="2" />
          <text x="265" y={y + 4} textAnchor="middle" fill="#00E5FF" fontSize="7" fontFamily="monospace">
            {['ROI', 'AUTO'][i]}
          </text>
        </g>
      ))}
    </g>

    {/* Ripple from centre hidden node */}
    <circle cx="160" cy="140" r="28" fill="none" stroke="#7C3AED" strokeWidth="1" opacity="0.4"
      style={{ animation: 'svgPulseSlow 2s ease-in-out infinite' }} />
    <circle cx="160" cy="140" r="44" fill="none" stroke="#00E5FF" strokeWidth="0.6" opacity="0.2"
      style={{ animation: 'svgPulseSlow 3s ease-in-out infinite 0.5s' }} />
  </svg>
);

/* ══════════════════════════════════════════════════
   05 — M365
   Visual: Hub-and-spoke with Microsoft app icons,
   animated orbit of apps around central M365 hub
══════════════════════════════════════════════════ */
const M365SVG = () => {
  const apps = [
    { label: 'Teams', icon: '💬', angle: 0 },
    { label: 'SharePoint', icon: '📂', angle: 60 },
    { label: 'Power Apps', icon: '⚡', angle: 120 },
    { label: 'Graph API', icon: '🔗', angle: 180 },
    { label: 'OneDrive', icon: '☁', angle: 240 },
    { label: 'Outlook', icon: '📧', angle: 300 },
  ];
  return (
    <svg viewBox="0 0 320 300" className="svc-svg" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="m365-grad" x1="0" y1="0" x2="1" y2="1">
          <stop stopColor="#7C3AED" /><stop offset="1" stopColor="#00E5FF" />
        </linearGradient>
        <filter id="m365-glow">
          <feGaussianBlur stdDeviation="4" result="b" />
          <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>

      <radialGradient id="m365-bg" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#00E5FF" stopOpacity="0.1" />
        <stop offset="100%" stopColor="transparent" />
      </radialGradient>
      <circle cx="160" cy="150" r="130" fill="url(#m365-bg)" />

      {/* Outer orbit ring */}
      <circle cx="160" cy="150" r="108" fill="none" stroke="url(#m365-grad)" strokeWidth="0.6" opacity="0.3"
        strokeDasharray="6 5" style={{ animation: 'orbit 30s linear infinite', transformOrigin: '160px 150px' }} />

      {/* Spoke lines from hub to apps */}
      {apps.map(({ angle }) => {
        const rad = (angle - 90) * Math.PI / 180;
        const x2 = 160 + 108 * Math.cos(rad);
        const y2 = 150 + 108 * Math.sin(rad);
        const x1 = 160 + 38 * Math.cos(rad);
        const y1 = 150 + 38 * Math.sin(rad);
        return (
          <line key={angle} x1={x1} y1={y1} x2={x2} y2={y2}
            stroke="url(#m365-grad)" strokeWidth="1" opacity="0.35"
            strokeDasharray="5 4"
            style={{ animation: `dashFlow ${1.5 + angle / 200}s linear infinite` }} />
        );
      })}

      {/* App nodes */}
      {apps.map(({ label, icon, angle }, i) => {
        const rad = (angle - 90) * Math.PI / 180;
        const cx = 160 + 108 * Math.cos(rad);
        const cy = 150 + 108 * Math.sin(rad);
        return (
          <g key={label} filter="url(#m365-glow)"
            style={{ animation: `floatY ${2.8 + i * 0.2}s ease-in-out infinite ${i * 0.3}s` }}>
            <circle cx={cx} cy={cy} r="26" fill="rgba(124,58,237,0.12)" stroke="url(#m365-grad)" strokeWidth="1.6" />
            <text x={cx} y={cy - 4} textAnchor="middle" fontSize="13">{icon}</text>
            <text x={cx} y={cy + 14} textAnchor="middle" fill="#00E5FF" fontSize="6.5" fontFamily="monospace">{label}</text>
          </g>
        );
      })}

      {/* Centre hub */}
      <g filter="url(#m365-glow)">
        <circle cx="160" cy="150" r="40" fill="rgba(124,58,237,0.2)" stroke="url(#m365-grad)" strokeWidth="2"
          style={{ animation: 'svgPulse 3s ease-in-out infinite' }} />
        <circle cx="160" cy="150" r="26" fill="rgba(0,229,255,0.1)" stroke="#00E5FF" strokeWidth="1.2" />
        <text x="160" y="147" textAnchor="middle" fill="#00E5FF" fontSize="9" fontFamily="monospace" fontWeight="bold">M365</text>
        <text x="160" y="160" textAnchor="middle" fill="rgba(255,255,255,0.5)" fontSize="7" fontFamily="monospace">Ecosystem</text>
      </g>
    </svg>
  );
};

/* ══════════════════════════════════════════════════
   06 — HEALTHCARE
   Visual: Shield with ECG + HIPAA lock +
   labelled compliance badges
══════════════════════════════════════════════════ */
const HealthcareSVG = () => (
  <svg viewBox="0 0 320 290" className="svc-svg" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="hc-grad" x1="0" y1="0" x2="1" y2="1">
        <stop stopColor="#7C3AED" /><stop offset="1" stopColor="#00E5FF" />
      </linearGradient>
      <filter id="hc-glow">
        <feGaussianBlur stdDeviation="5" result="b" />
        <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
      </filter>
    </defs>

    <radialGradient id="hc-bg" cx="50%" cy="46%" r="50%">
      <stop offset="0%" stopColor="#00E5FF" stopOpacity="0.1" />
      <stop offset="100%" stopColor="transparent" />
    </radialGradient>
    <circle cx="160" cy="135" r="120" fill="url(#hc-bg)" />

    {/* Shield body */}
    <g filter="url(#hc-glow)">
      <path d="M160 18 L228 50 L228 128 Q228 185 160 210 Q92 185 92 128 L92 50 Z"
        fill="rgba(124,58,237,0.1)" stroke="url(#hc-grad)" strokeWidth="2.2"
        style={{ animation: 'svgPulseSlow 3s ease-in-out infinite' }} />
      {/* Inner shield outline */}
      <path d="M160 32 L215 60 L215 126 Q215 172 160 194 Q105 172 105 126 L105 60 Z"
        fill="none" stroke="rgba(0,229,255,0.2)" strokeWidth="1" />
    </g>

    {/* ECG heartbeat line — animated draw */}
    <g filter="url(#hc-glow)">
      <polyline
        points="100,128 114,128 120,108 128,150 136,90 144,162 150,118 160,118 168,128 180,128"
        fill="none" stroke="#00E5FF" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
        strokeDasharray="300" strokeDashoffset="0"
        style={{ animation: 'ecgDraw 2s ease-in-out infinite' }} />
    </g>

    {/* Medical cross */}
    <g filter="url(#hc-glow)" opacity="0.6">
      <rect x="152" y="170" width="16" height="36" rx="4" fill="none" stroke="url(#hc-grad)" strokeWidth="1.8" />
      <rect x="140" y="182" width="40" height="14" rx="4" fill="none" stroke="url(#hc-grad)" strokeWidth="1.8" />
    </g>

    {/* HIPAA Lock badge — top right of shield */}
    <g filter="url(#hc-glow)" style={{ animation: 'floatY 3s ease-in-out infinite 0.5s' }}>
      <rect x="194" y="42" width="50" height="38" rx="8" fill="rgba(0,229,255,0.1)" stroke="#00E5FF" strokeWidth="1.4" />
      <text x="219" y="57" textAnchor="middle" fill="#00E5FF" fontSize="7.5" fontFamily="monospace" fontWeight="bold">HIPAA</text>
      <text x="219" y="70" textAnchor="middle" fill="rgba(255,255,255,0.5)" fontSize="6.5" fontFamily="monospace">Compliant</text>
      {/* lock icon */}
      <rect x="212" y="74" width="14" height="10" rx="2" fill="none" stroke="#00E5FF" strokeWidth="1" />
      <path d="M215 74 Q215 69 219 69 Q223 69 223 74" fill="none" stroke="#00E5FF" strokeWidth="1" />
    </g>

    {/* Badges below shield */}
    {[
      { x: 65, label: 'HL7/FHIR' },
      { x: 160, label: 'EHR Integration' },
      { x: 258, label: 'Clinical Flows' },
    ].map(({ x, label }) => (
      <g key={label} filter="url(#hc-glow)" style={{ animation: 'floatY 3s ease-in-out infinite' }}>
        <rect x={x - 46} y="224" width="92" height="26" rx="7"
          fill="rgba(124,58,237,0.12)" stroke="url(#hc-grad)" strokeWidth="1.2" />
        <text x={x} y="241" textAnchor="middle" fill="#A78BFA" fontSize="7.5" fontFamily="monospace">{label}</text>
      </g>
    ))}
  </svg>
);

/* ══════════════════════════════════════════════════
   MAIN APP
══════════════════════════════════════════════════ */
function App() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  return (
    <>
      <SvgStyles />


      {/* NAV */}
      <nav>
        <div className="nav-inner">
          <a href="/" className="nav-logo" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <img src="/amatres-logo2.jpg" alt="Amatres" style={{ width: '36px', height: '36px', objectFit: 'contain' }} />
            <span>Amatres<strong> Technologies</strong></span>
          </a>
          <div className={`mobile-menu-toggle ${isMobileMenuOpen ? 'open' : ''}`}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
            <span /><span /><span />
          </div>
          <ul className={`nav-links ${isMobileMenuOpen ? 'active' : ''}`}>
            <li><a href="#services" onClick={(e) => { e.preventDefault(); setIsMobileMenuOpen(false); scrollTo('services'); }}>Services</a></li>
            <li><a href="#expertise" onClick={(e) => { e.preventDefault(); setIsMobileMenuOpen(false); scrollTo('expertise'); }}>Expertise</a></li>
            <li><a href="#why" onClick={(e) => { e.preventDefault(); setIsMobileMenuOpen(false); scrollTo('why'); }}>Why Amatres</a></li>
            <li>
              <a href="/contact" onClick={() => setIsMobileMenuOpen(false)}
                style={{
                  background: 'linear-gradient(135deg,#7C3AED,#00E5FF)',
                  color: '#fff',
                  padding: '8px 18px',
                  borderRadius: '8px',
                  fontWeight: 600,
                  letterSpacing: '0.03em',
                  transition: 'opacity 0.2s',
                  whiteSpace: 'nowrap'
                }}
                onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
                onMouseLeave={e => e.currentTarget.style.opacity = '1'}
              >Let's Talk ↗</a>
            </li>
          </ul>
        </div>
      </nav>

      {/* HERO */}
      <section id="hero">
        <div className="spline-bg" style={{ position: 'absolute', top: '-10%', left: '-30%', right: '-30%', bottom: '-600px', zIndex: 0, pointerEvents: 'none', minWidth: '160%', height: '150%' }}>          
          <Spline scene="https://prod.spline.design/DLW6WsJqDxf-K3Fk/scene.splinecode" />
        </div>      
          <div className="container" style={{ position: 'relative', zIndex: 10 }}>
          <div className="hero-inner">
            <div className="hero-content">
              <h1 className="hero-h1">
                Precision. <br className="mobile-break" />
                Reliability. <br className="mobile-break" />
                <span className="h-white">Scale.</span>
              </h1>
              <p className="hero-desc">We build enterprise-grade software and AI systems for teams that demand excellence, clear scope, disciplined execution, production-ready delivery.</p>
              <div className="hero-cta" style={{ display: 'flex', gap: '16px' }}>
                <a href="/contact" className="btn-grad">Get in Touch →</a>
                <HashLink smooth to="/#services" className="btn-outline">View Services ↓</HashLink>              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS STRIP */}
      <div className="stats-strip">
        <div className="stats-inner">
          <div className="stat"><span className="stat-title">QA & TESTING</span><span className="stat-desc">Manual • Automation • API</span></div>
          <div className="stat"><span className="stat-title">FULL STACK</span><span className="stat-desc">MERN • PHP • MySQL</span></div>
          <div className="stat"><span className="stat-title">PRODUCT THINKING</span><span className="stat-desc">UX • Flows • Roadmaps</span></div>
          <div className="stat"><span className="stat-title">PROBLEM SOLVING</span><span className="stat-desc">DSA • Optimization</span></div>
        </div>
      </div>

      {/* SERVICES */}
      <section id="services">
        <div className="sec-orb" />
        <div className="container">
          <div className="sec-label">
            <span className="sec-label-num">01 //</span>
            <span className="sec-label-text">What We Do</span>
            <div className="sec-label-line" />
          </div>
          <div className="ghost-head ghost-dark services-ghost">SERVICES</div>
          <h2 className="sec-hl sec-hl-dark services-hl">Built Around<br /><em>Your Challenges.</em></h2>
          <p className="services-subhead">Clear scope, disciplined execution, and production-ready delivery — from strategy to implementation.</p>

          {/* 01 — Tech Consulting */}
          <div className="svc-row" id="tech-consulting">
            <div className="svc-ghost-num">01</div>
            <div className="svc-info"><div>
              <div className="svc-tag">Tech Consulting</div>
              <div className="svc-title">Pragmatic Guidance to<br /><em>Reduce Risk.</em></div>
              <p className="svc-desc">Architecture reviews, modernization planning, and system design guidance to reduce risk and accelerate delivery.</p>
              <ul className="svc-bullets">
                <li>Architecture reviews</li><li>Modernization roadmap</li>
                <li>Performance &amp; reliability audits</li><li>System design guidance</li>
              </ul>
            </div></div>
            <div className="svc-visual"><TechConsultingSVG /></div>
          </div>

          {/* 02 — Product Engineering */}
          <div className="svc-row alt" id="product-engineering">
            <div className="svc-ghost-num">02</div>
            <div className="svc-visual"><ProductEngineeringSVG /></div>
            <div className="svc-info"><div>
              <div className="svc-tag">End-to-End Product Engineering</div>
              <div className="svc-title">Build from MVP<br />to <em>Scale.</em></div>
              <p className="svc-desc">Senior-led execution across the full product lifecycle — from first commit to production at scale.</p>
              <ul className="svc-bullets">
                <li>MVP development</li><li>Full-stack engineering</li>
                <li>Quality &amp; testing strategy</li><li>Scale &amp; optimization</li>
              </ul>
            </div></div>
          </div>

          {/* 03 — Cloud & DevOps */}
          <div className="svc-row" id="cloud-devops">
            <div className="svc-ghost-num">03</div>
            <div className="svc-info"><div>
              <div className="svc-tag">Cloud Architecture &amp; DevOps</div>
              <div className="svc-title">Secure, Observable<br /><em>Cloud Foundations.</em></div>
              <p className="svc-desc">Cloud-native architectures built to last — reliable, cost-optimized, and observable from day one.</p>
              <ul className="svc-bullets">
                <li>CI/CD pipelines</li><li>Infrastructure as code</li>
                <li>Observability &amp; monitoring</li><li>Cost optimization</li>
              </ul>
            </div></div>
            <div className="svc-visual"><CloudDevOpsSVG /></div>
          </div>

          {/* 04 — AI Automation */}
          <div className="svc-row alt" id="ai-automation">
            <div className="svc-ghost-num">04</div>
            <div className="svc-visual"><AIDrivenSVG /></div>
            <div className="svc-info"><div>
              <div className="svc-tag">AI-Driven Automation</div>
              <div className="svc-title">LLM Integrations that<br /><em>Deliver ROI.</em></div>
              <p className="svc-desc">Purposeful AI implementation — not gimmicks. Real workflows, real automation, measurable gains.</p>
              <ul className="svc-bullets">
                <li>Workflow automation</li><li>LLM integrations</li>
                <li>Data pipelines &amp; insights</li><li>Responsible AI enablement</li>
              </ul>
            </div></div>
          </div>

          {/* 05 — M365 */}
          <div className="svc-row" id="m365-development">
            <div className="svc-ghost-num">05</div>
            <div className="svc-info"><div>
              <div className="svc-tag">M365 Development</div>
              <div className="svc-title">Enterprise Workflows<br />on <em>Microsoft 365.</em></div>
              <p className="svc-desc">Deep expertise across the full M365 ecosystem — SharePoint, Teams, Power Platform, and Graph API.</p>
              <ul className="svc-bullets">
                <li>Power Platform solutions</li><li>M365 workflows</li>
                <li>Provisioning automation</li><li>Enterprise integrations</li>
              </ul>
            </div></div>
            <div className="svc-visual"><M365SVG /></div>
          </div>

          {/* 06 — Healthcare */}
          <div className="svc-row alt" id="healthcare-tech">
            <div className="svc-ghost-num">06</div>
            <div className="svc-visual"><HealthcareSVG /></div>
            <div className="svc-info"><div>
              <div className="svc-tag">Healthcare Tech</div>
              <div className="svc-title">Secure Systems for<br /><em>Clinical Workflows.</em></div>
              <p className="svc-desc">HIPAA-compliant systems and EHR integrations built with the regulatory rigor that healthcare demands.</p>
              <ul className="svc-bullets">
                <li>Patient portals</li><li>Real-time tracking</li>
                <li>Compliance-first architecture</li><li>Clinical integrations</li>
              </ul>
            </div></div>
          </div>

        </div>
      </section>

      {/* ENGAGEMENTS */}
      <div className="engagements">
        <div className="eng-header">
          <div className="eng-sec-label"><span>— //</span><span>Engagements</span><div className="eng-sec-label-line" /></div>
          <div className="ghost-head ghost-light eng-ghost-head">ENGAGEMENTS</div>
          <h2 className="eng-hl">Start Small.<br /><em>Scale Fast.</em></h2>
        </div>
        <div className="eng-grid">
          {[
            { n: '01', name: 'Discovery', desc: 'Roadmap, architecture, milestones, and a delivery plan aligned to your constraints — before a single line of code.' },
            { n: '02', name: 'Delivery Sprint', desc: 'A defined scope build with implementation, testing, and release readiness. Shipped on time, owned end-to-end.' },
            { n: '03', name: 'Retainer', desc: 'Ongoing engineering ownership, optimization, and continuous improvement. We stay in it as your platform grows.' },
          ].map(({ n, name, desc }) => (
            <div className="eng-card" key={n}>
              <div className="eng-ghost-name">{name}</div>
              <div className="eng-ghost-num">{n}</div>
              <div className="eng-content">
                <span className="eng-num">{n}</span>
                <p className="eng-desc">{desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* EXPERTISE */}
      <section id="expertise">
        <div className="sec-orb" />
        <div className="container">
          <div className="exp-sec-label"><span>02 //</span><span>Deep Capabilities</span><div className="exp-sec-label-line" /></div>
          <div className="ghost-head ghost-light exp-ghost-head">EXPERTISE</div>
          <h2 className="sec-hl exp-hl-text">Where We<br /><em>Go Deep.</em></h2>
        </div>
        {[
          { n: '01', title: 'AWS & Cloud-Native Systems', body: 'Architecting and deploying scalable, resilient infrastructure on AWS using serverless, containers, managed services, and infrastructure-as-code.', tags: ['Lambda', 'ECS/EKS', 'Terraform', 'CDK'], alt: false },
          { n: '02', title: 'M365 Development', body: 'Deep expertise in the Microsoft 365 ecosystem — SharePoint, Teams apps, Power Platform, and Graph API integrations that extend enterprise workflows.', tags: ['SharePoint', 'Teams', 'Power Platform', 'Graph API'], alt: true },
          { n: '03', title: 'Product Engineering', body: 'Full-stack product development from design to deployment — APIs, frontends, mobile apps, and backend services built for maintainability and long-term quality.', tags: ['React', 'Node.js', '.NET', 'REST / GraphQL'], alt: false },
          { n: '04', title: 'Healthcare Technology', body: 'HIPAA-compliant systems, EHR integrations, and health data pipelines built with the domain sensitivity and regulatory rigor healthcare demands.', tags: ['HIPAA', 'HL7 / FHIR', 'EHR Integration'], alt: true },
          { n: '05', title: 'AI & LLM Integrations', body: 'Practical AI implementation — RAG pipelines, LLM-powered workflows, and custom model integrations that deliver measurable productivity gains.', tags: ['OpenAI', 'RAG Pipelines', 'Agents', 'Fine-tuning'], alt: false },
          { n: '06', title: 'Delivery & Project Management', body: 'Structured delivery with agile discipline. Clear milestones, transparent communication, and proactive risk management throughout.', tags: ['Agile', 'Scrum', 'Risk Mgmt'], alt: true },
        ].map(({ n, title, body, tags, alt }) => (
          <div className={`exp-row${alt ? ' alt' : ''}`} key={n}>
            <div className="exp-ghost-num">{n}</div>
            <div className="exp-card">
              <div className="exp-num-box">{n}</div>
              <div className="exp-body">
                <h3>{title}</h3>
                <p>{body}</p>
                <div className="etags">{tags.map(t => <span className="etag" key={t}>{t}</span>)}</div>
              </div>
            </div>
          </div>
        ))}
      </section>

      {/* WHY AMATRES */}
      <section id="why">
        <div className="sec-orb" />
        <div className="container">
          <div className="sec-label">
            <span className="sec-label-num">03 //</span>
            <span className="sec-label-text">Why Amatres</span>
            <div className="sec-label-line" />
          </div>
          <div className="ghost-head ghost-dark why-ghost">PRINCIPLES</div>
          <h2 className="sec-hl sec-hl-dark why-hl">Principles,<br /><em>Not Trends.</em></h2>
          <div className="why-grid">
            {[
              {
                n: '01', title: 'Senior-Led Execution', desc: 'No junior hand-offs. Every project is led and delivered by senior engineers with real enterprise depth.',
                icon: <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.5"><circle cx="12" cy="8" r="4" stroke="#FF4B1F" /><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" stroke="#4169FF" /></svg>
              },
              {
                n: '02', title: 'Pragmatic Architecture', desc: "Right-sized decisions. We design systems that match your actual needs — scalable but not over-engineered.",
                icon: <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.5"><path d="M12 2L2 7l10 5 10-5-10-5z" stroke="#FF4B1F" /><path d="M2 17l10 5 10-5M2 12l10 5 10-5" stroke="#4169FF" /></svg>
              },
              {
                n: '03', title: 'Cloud & DevOps Ready', desc: 'Infrastructure-as-code, automated pipelines, and observability baked in from the very start.',
                icon: <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.5"><rect x="3" y="3" width="7" height="7" stroke="#FF4B1F" rx="1" /><rect x="14" y="14" width="7" height="7" stroke="#4169FF" rx="1" /><rect x="14" y="3" width="7" height="7" stroke="#4169FF" rx="1" opacity="0.5" /><rect x="3" y="14" width="7" height="7" stroke="#FF4B1F" rx="1" opacity="0.5" /></svg>
              },
              {
                n: '04', title: 'AI Where It Matters', desc: 'LLMs and automation integrated purposefully — not as a gimmick, but to create measurable value.',
                icon: <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.5"><circle cx="12" cy="12" r="3" stroke="#FF4B1F" /><path d="M12 2v3M12 19v3M2 12h3M19 12h3" stroke="#4169FF" /></svg>
              },
              {
                n: '05', title: 'Domain-Driven Thinking', desc: 'We learn your domain before writing a line of code. The model always matches the business.',
                icon: <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.5"><path d="M3 5h18M3 12h18M3 19h12" stroke="#FF4B1F" /></svg>
              },
              {
                n: '06', title: 'Partner Mindset', desc: "We're in it with you — from inception through growth and scale, not just until launch.",
                icon: <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.5"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" stroke="#FF4B1F" /><circle cx="9" cy="7" r="4" stroke="#FF4B1F" /><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" stroke="#4169FF" /></svg>
              },
            ].map(({ n, title, desc, icon }) => (
              <div className="why-card" key={n}>
                <div className="wc-num">{n}</div>
                <div className="wc-icon">{icon}</div>
                <h3>{title}</h3>
                <p>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="cta" className="cta-section">
        <div className="sec-orb" style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: '500px', height: '500px', zIndex: 0, borderRadius: '50%', filter: 'blur(100px)', background: 'linear-gradient(135deg,rgba(124,58,237,0.15),rgba(0,229,255,0.15))', pointerEvents: 'none' }} />
        <div className="container" style={{ position: 'relative', zIndex: 10 }}>
          <div className="glass-cta-card">
            <div className="cta-content">
              <h2 className="cta-hl">Ready to <em>Scale?</em></h2>
              <p className="cta-desc">Let's engineer reliable solutions together.</p>
            </div>
            <div className="cta-actions">
              <a href="/contact" className="btn-grad">Let's Build <span>→</span></a>
              <a href="https://mail.google.com/mail/?view=cm&to=connect@amatrestechnologies.in&su=Project%20Inquiry" target="_blank" rel="noreferrer" className="btn-outline">Contact Us</a>            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer>
        <div className="container" style={{ position: 'relative', zIndex: 10 }}>
          <div className="footer-top">
            <div>
              <div className="footer-brand-name">Amatres Technologies<span>.</span></div>
              <p className="footer-tagline">Engineering reliable products for teams that care about quality. Bengaluru, India.</p>
            </div>
            <div className="footer-col">
              <div className="footer-col-title">Company</div>
              <ul>
                <li><a href="#services" onClick={(e) => { e.preventDefault(); scrollTo('services'); }}>Services</a></li>
                <li><a href="#expertise" onClick={(e) => { e.preventDefault(); scrollTo('expertise'); }}>Expertise</a></li>
                <li><a href="#why" onClick={(e) => { e.preventDefault(); scrollTo('why'); }}>Why Amatres</a></li>
                <li><a href="#cta" onClick={(e) => { e.preventDefault(); scrollTo('cta'); }}>Contact</a></li>
              </ul>
            </div>
            <div className="footer-col">
              <div className="footer-col-title">Services</div>
              <ul>
                <li><a href="#tech-consulting">Tech Consulting</a></li>
                <li><a href="#product-engineering">Product Engineering</a></li>
                <li><a href="#cloud-devops">Cloud &amp; DevOps</a></li>
                <li><a href="#ai-automation">AI-Driven Automation</a></li>
                <li><a href="#m365-development">M365 Development</a></li>
                <li><a href="#healthcare-tech">Healthcare Tech</a></li>
              </ul>
            </div>
          </div>
          <div className="footer-btm">
            <div className="footer-copy">© {new Date().getFullYear()} Amatres Technologies. All rights reserved.</div>
            <div className="footer-contact"><a href="mailto:connect@amatrestechnologies.in">connect@amatrestechnologies.in</a> &nbsp;·&nbsp; Bengaluru, India</div>
          </div>
        </div>
      </footer>
    </>
  );
}

export default App;